import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TIP_SELECTOR, captureTitle, restoreTitle, restoreAllTitles } from '../utils/tooltipTitle';
import { visibleViewportBox } from '../utils/viewport';

/** 마우스를 올린 뒤 툴팁이 뜨기까지 (브라우저 기본은 1~2초이고 조절 불가라 직접 그린다) */
const OPEN_DELAY = 150;
/** 직전 툴팁을 닫은 지 이 시간 안이면 지연 없이 표시 — 툴바 아이콘을 훑을 때 끊기지 않게 */
const WARM_MS = 300;
/** 대상과 툴팁 사이 간격 */
const GAP = 8;
/** 화면 가장자리 여백 (ContextMenu의 클램핑과 동일 값) */
const EDGE = 4;
/** 워치독 주기 — 이벤트가 오지 않는 경로(언마운트·비활성화)를 잡는 2차 방어 */
const WATCH_MS = 250;

type Placement = 'top' | 'bottom' | 'left' | 'right';

/**
 * 전역 커스텀 툴팁.
 *
 * 앱 전체의 `title` 속성(69곳)을 마크업 수정 없이 가로채 빠르게 띄운다.
 * hover 동안 title을 DOM에서 떼야만 네이티브 툴팁이 겹치지 않으므로,
 * 복원 실패 = 툴팁 영구 소실이다. 복원 경로를 여러 겹으로 두는 이유가 그것이다.
 *
 * 배치·포탈·클램핑은 ContextMenu.tsx와 같은 방식을 쓴다 (조상의 transform이
 * position:fixed 기준을 깨고 루트 overflow-hidden이 잘라내므로 body 포탈이 필수).
 */
export const GlobalTooltip: React.FC = () => {
  const [tip, setTip] = useState<{ text: string; el: HTMLElement } | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number; ready: boolean }>({ x: 0, y: 0, ready: false });

  const boxRef = useRef<HTMLDivElement>(null);
  /** 현재 겨냥 중인 요소 (표시 전 대기 상태 포함) */
  const targetRef = useRef<HTMLElement | null>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const watchdogRef = useRef<number | undefined>(undefined);
  const moRef = useRef<MutationObserver | null>(null);
  /** 실제로 화면에 떴는지 (warm window 판정용 — 뜨지도 않았는데 즉시 표시하면 안 된다) */
  const shownRef = useRef(false);
  const lastHideRef = useRef(0);

  const hide = useCallback(() => {
    window.clearTimeout(timerRef.current);
    timerRef.current = undefined;
    window.clearInterval(watchdogRef.current);
    watchdogRef.current = undefined;
    if (moRef.current) {
      moRef.current.disconnect();
      moRef.current = null;
    }

    const el = targetRef.current;
    targetRef.current = null;
    if (el) restoreTitle(el);
    // 안전망: 어떤 경로로든 놓친 백업까지 회수 (정상 상태에서 대상은 최대 1개)
    restoreAllTitles();

    if (shownRef.current) lastHideRef.current = Date.now();
    shownRef.current = false;
    setTip(null);
    setPos((p) => (p.ready ? { ...p, ready: false } : p));
  }, []);

  const show = useCallback(
    (el: HTMLElement) => {
      // 지연 대기 중 대상이 바뀌었거나 사라졌으면 취소
      if (targetRef.current !== el || !el.isConnected) return;
      const text = captureTitle(el);
      if (!text) return;

      shownRef.current = true;
      setTip({ text, el });

      // title이 동적으로 바뀌거나(리액션 명단 등) 버튼이 살아있는 채로 disabled 되는 경우를 감시.
      // 특히 후자는 mouseout이 오지 않아 이 감시가 없으면 title이 복원되지 못한다.
      const mo = new MutationObserver(() => {
        if (!el.isConnected || (el as HTMLButtonElement).disabled) {
          hide();
          return;
        }
        if (el.hasAttribute('title')) {
          const fresh = captureTitle(el); // 새 title을 백업·제거하고 값을 돌려준다
          if (fresh) setTip({ text: fresh, el });
          else hide();
        }
      });
      mo.observe(el, { attributes: true, attributeFilter: ['title', 'disabled'] });
      moRef.current = mo;

      // 이벤트가 전혀 오지 않는 경로(조건부 렌더로 사라짐 등) 대비
      watchdogRef.current = window.setInterval(() => {
        const t = targetRef.current;
        if (!t) return;
        if (!t.isConnected || (t as HTMLButtonElement).disabled || t.getBoundingClientRect().width === 0) {
          hide();
        }
      }, WATCH_MS);
    },
    [hide]
  );

  useEffect(() => {
    const onOver = (e: PointerEvent) => {
      // 터치·펜 배제: hover가 없는 입력에서는 title을 건드리지도, 툴팁을 띄우지도 않는다.
      // 뷰포트 폭이 아니라 이벤트별 입력 종류로 갈라야 태블릿+마우스 같은 조합이 올바르게 동작한다.
      if (e.pointerType !== 'mouse') return;

      const node = e.target as Element | null;
      if (!node || typeof node.closest !== 'function') return;

      const current = targetRef.current;
      // 활성 대상 안에서 움직이는 중이면 무시 (title을 뗀 상태라 재탐색하면 어긋난다)
      if (current && current.contains(node)) return;

      const next = node.closest(TIP_SELECTOR) as HTMLElement | null;
      if (next === current) return;

      if (current) hide();
      if (!next || next.hasAttribute('data-tooltip-off')) return;

      targetRef.current = next;
      const warm = Date.now() - lastHideRef.current < WARM_MS;
      timerRef.current = window.setTimeout(() => show(next), warm ? 0 : OPEN_DELAY);
    };

    const onOut = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const current = targetRef.current;
      if (!current) return;
      const to = e.relatedTarget as Node | null;
      // relatedTarget이 null이면 창 밖으로 나간 경우
      if (!to || !current.contains(to)) hide();
    };

    const onKeyDown = () => {
      if (targetRef.current) hide();
    };

    // 전부 capture 단계 — 하위 핸들러의 stopPropagation에 가려지지 않게 한다.
    // 특히 click은 React의 위임 핸들러보다 먼저 실행돼야 요소가 언마운트되기 전에 title을 복원한다.
    document.addEventListener('pointerover', onOver, true);
    document.addEventListener('pointerout', onOut, true);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('click', hide, true);
    document.addEventListener('keydown', onKeyDown, true);
    // scroll은 버블하지 않으므로 capture로 받아야 내부 스크롤 컨테이너까지 잡힌다 (ContextMenu와 동일)
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    window.addEventListener('blur', hide);
    document.addEventListener('visibilitychange', hide);
    window.addEventListener('pagehide', restoreAllTitles);

    return () => {
      document.removeEventListener('pointerover', onOver, true);
      document.removeEventListener('pointerout', onOut, true);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('click', hide, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('blur', hide);
      document.removeEventListener('visibilitychange', hide);
      window.removeEventListener('pagehide', restoreAllTitles);
      hide();
      restoreAllTitles();
    };
  }, [hide, show]);

  // 위치 계산: 커서가 아니라 대상 요소 기준 (커서 추적형은 커서를 가리고 흔들린다)
  useLayoutEffect(() => {
    const box = boxRef.current;
    const el = tip?.el;
    if (!box || !el) return;

    const r = el.getBoundingClientRect();
    const { width: w, height: h } = box.getBoundingClientRect();
    // 보이는 영역을 layout viewport(fixed 좌표계) 기준으로 — window.innerWidth/innerHeight는
    // iOS 키보드가 뜨면 visual viewport를 따라가 좌표계가 어긋난다 (ContextMenu와 동일 규칙)
    const vp = visibleViewportBox();
    const vLeft = vp.left + EDGE;
    const vRight = vp.left + vp.width;
    const vTop = vp.top + EDGE;
    const vBottom = vp.top + vp.height;
    const placement = (el.getAttribute('data-tooltip-placement') as Placement | null) || 'bottom';

    let x: number;
    let y: number;

    if (placement === 'right' || placement === 'left') {
      x = placement === 'right' ? r.right + GAP : r.left - GAP - w;
      y = r.top + r.height / 2 - h / 2;
      // 공간이 없으면 반대편으로 뒤집는다
      if (placement === 'right' && x + w + EDGE > vRight) x = r.left - GAP - w;
      if (placement === 'left' && x < vLeft) x = r.right + GAP;
    } else {
      x = r.left + r.width / 2 - w / 2;
      const below = r.bottom + GAP;
      const above = r.top - GAP - h;
      if (placement === 'top') {
        y = above >= vTop ? above : below;
      } else {
        y = below + h + EDGE <= vBottom ? below : above >= vTop ? above : below;
      }
    }

    setPos({
      x: Math.max(vLeft, Math.min(x, vRight - w - EDGE)),
      y: Math.max(vTop, Math.min(y, vBottom - h - EDGE)),
      ready: true,
    });
  }, [tip]);

  if (!tip) return null;

  return createPortal(
    <div
      ref={boxRef}
      role="tooltip"
      // pointer-events-none 필수: 툴팁이 커서 아래로 들어오면 pointerout → 깜빡임 루프가 된다
      className={`fixed z-[70] pointer-events-none select-none max-w-[280px] px-2 py-1 rounded-md bg-chrome-line text-white text-xs leading-snug shadow-lg break-words whitespace-pre-line ${
        pos.ready ? 'animate-tooltip-in' : ''
      }`}
      // 측정 전(ready=false)에는 숨겨둔다 — 작아서 위치가 튀는 게 눈에 띈다
      style={{ left: pos.x, top: pos.y, opacity: pos.ready ? undefined : 0 }}
    >
      {tip.text}
    </div>,
    document.body
  );
};
